import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { ArrowPathIcon, ClipboardIcon } from '@heroicons/react/24/outline';
import {
  CheckBadgeIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'motion/react';
import { useLayoutContext } from '~/components/layout/Layout';
import { APP_DOMAINS } from '~/types';
import {
  GET_AGENT_DETAILS,
  GEN_SHOPIFY_INTEGRATION_CODE,
  UPDATE_SKIP_SHOPIFY,
} from '~/services/agents';
import { GET_INTEGRATIONS } from '~/services/integration';
import { GET_ADVANCE_SETTINGS } from '~/services';
import WhatsAppIcon from '~/assets/icons/WhatsAppIcon';
import InstagramIcon from '~/assets/icons/InstagramIcon';
import {
  handleConnectWhatsapp,
  handleConnectInstagram,
} from '../integrations/integration-handlers';
import { REGISTER_META_TOKEN } from '~/services/meta';

// Animation variants for orchestrated entrance
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 300,
    },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.9,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 400,
    },
  },
  hover: {
    y: -2,
    scale: 1.02,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 400,
    },
  },
};

const quickActionVariants = {
  hidden: {
    opacity: 0,
    scale: 0.8,
    rotateX: -15,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotateX: 0,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 300,
    },
  },
  hover: {
    scale: 1.05,
    y: -2,
    transition: {
      type: 'spring',
      damping: 15,
      stiffness: 400,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      type: 'spring',
      damping: 15,
      stiffness: 400,
    },
  },
};

const AgentDashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { renderToast } = useLayoutContext();
  const { agentId } = useParams();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<any>(null);
  const [business, setBusiness] = useState<any>(null);
  const [newlyCreatedBusiness, setNewlyCreatedBusiness] = useState(false);
  const [code, setCode] = useState('');
  const [shopName, setShopName] = useState('');
  const [isShopifyNotConnected, setIsShopifyNotConnected] = useState(true);
  const [isIntegrationRefreshing, setIsIntegrationRefreshing] = useState(false);
  const [showAllWhatsapp, setShowAllWhatsapp] = useState(false);
  const [showAllInstagram, setShowAllInstagram] = useState(false);

  const [registerToken] = useMutation(REGISTER_META_TOKEN);
  const [
    getIntegrations,
    { data: integrationsData, loading: integrationsLoading },
  ] = useLazyQuery(GET_INTEGRATIONS, {
    variables: { data: { agent_id: agentId } },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (agentId) getIntegrations();
  }, [agentId]);

  // Determine which channels are not connected
  const enabledIntegrations = integrationsData?.get_integrations?.data || [];
  const whatsappIntegrations = enabledIntegrations.filter(
    (i: any) => i.integration_name === 'whatsapp',
  );
  const instagramIntegrations = enabledIntegrations.filter(
    (i: any) => i.integration_name === 'instagram',
  );
  const isWhatsappConnected = whatsappIntegrations.length > 0;
  const isInstagramConnected = instagramIntegrations.length > 0;

  const whatsappRequirements = t(
    'onboarding:integration_cards.whatsapp.requirements',
    { returnObjects: true },
  );
  const whatsappReqList = Array.isArray(whatsappRequirements)
    ? whatsappRequirements
    : [];
  const instagramRequirements = t(
    'onboarding:integration_cards.instagram.requirements',
    { returnObjects: true },
  );
  const instagramReqList = Array.isArray(instagramRequirements)
    ? instagramRequirements
    : [];

  // Channels state for settings display (to reflect enabled/disabled colors)
  const { data: advanceSettingsData } = useQuery(GET_ADVANCE_SETTINGS, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
  });
  const channelsList = (advanceSettingsData?.get_advance_settings?.data
    ?.channels || []) as any[];

  // Get agent details
  const [getAgentDetails, { loading: loadingAgentDetails }] = useLazyQuery(
    GET_AGENT_DETAILS,
    {
      variables: {
        data: {
          agent_id: agentId,
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        if (data.response?.data) {
          setAgent(data.response.data);
          if (data.response.data.newlyCreatedBusiness) {
            setNewlyCreatedBusiness(true);
            renderToast({
              type: 'success',
              message: t('dashboard:business_created'),
            });
          }
          if (data.response.data.business) {
            setBusiness(data.response.data.business);
            setShopName(data.response.data.business.shopify?.shop_name || '');
            setIsShopifyNotConnected(
              !data.response.data.business.shopify?.shop_name,
            );
          }
          setIsIntegrationRefreshing(false);
        }
      },
      onError: (error) => {
        console.error('Error fetching agent details:', error);
        renderToast({
          type: 'error',
          message: t('dashboard:error.load_agent'),
        });
        setIsIntegrationRefreshing(false);
      },
    },
  );

  // Generate Shopify integration code
  const [genCode, { loading: loadingGenCode }] = useMutation(
    GEN_SHOPIFY_INTEGRATION_CODE,
    {
      onCompleted: (data) => {
        if (data.response?.data?.shopify_code) {
          setCode(data.response.data.shopify_code);
        }
      },
      onError: (error) => {
        console.error('Error generating Shopify code:', error);
        renderToast({
          type: 'error',
          message: t('dashboard:error.gen_code'),
        });
      },
    },
  );

  // Skip Shopify integration
  const [skipShopify, { loading: loadingSkipShopify }] = useMutation(
    UPDATE_SKIP_SHOPIFY,
    {
      onCompleted: (data) => {
        if (data.response?.data) {
          renderToast({
            type: 'success',
            message: t('dashboard:shopify.skip'),
          });
          setIsShopifyNotConnected(false);
        }
      },
      onError: (error) => {
        console.error('Error skipping Shopify integration:', error);
        renderToast({
          type: 'error',
          message: t('dashboard:error.skip_integration'),
        });
      },
    },
  );

  const copyCodeToClipboard = () => {
    navigator.clipboard.writeText(code);
    renderToast({
      type: 'success',
      message: t('dashboard:shopify.code_copied'),
    });
  };

  const goToChat = () => {
    navigate(`/agent/${agentId}/${APP_DOMAINS.CONVERSATIONS}`);
  };

  const refreshBusinessStatus = () => {
    setIsIntegrationRefreshing(true);
    getAgentDetails();
  };

  useEffect(() => {
    if (agentId) {
      getAgentDetails();
    }
  }, [agentId]);

  useEffect(() => {
    if (agentId && business?._id && isShopifyNotConnected) {
      genCode({
        variables: {
          input: {
            business_id: business._id,
          },
        },
      });
    }
  }, [business?._id, isShopifyNotConnected, agentId]);

  const isLoading = loadingAgentDetails || loadingGenCode || loadingSkipShopify;

  return (
    <motion.div
      className="mx-auto mb-24 flex h-full w-full max-w-[1200px] flex-col p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="mb-8" variants={itemVariants}>
        <motion.h1
          className="text-3xl font-bold"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, type: 'spring', damping: 20 }}
        >
          {agent?.agent.name || t('dashboard:title')} {t('dashboard:dashboard')}
        </motion.h1>
        <motion.p
          className="text-base-content"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, type: 'spring', damping: 20 }}
        >
          {t('dashboard:subtitle')}
        </motion.p>
      </motion.div>

      <AnimatePresence>
        {newlyCreatedBusiness && (
          <motion.div
            className="border-base-300 bg-base-100 text-base-content mb-6 rounded-lg border p-4"
            variants={itemVariants}
            initial="hidden"
            animate="visible"
            exit={{
              opacity: 0,
              scale: 0.95,
              y: -10,
              transition: { duration: 0.2 },
            }}
            whileHover={{ scale: 1.01 }}
          >
            <div className="flex items-center">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <InformationCircleIcon className="mr-3 h-5 w-5" />
              </motion.div>
              <p>{t('dashboard:legacy_business_created')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2"
        variants={containerVariants}
      >
        <motion.div
          className="bg-base-100 border-base-300 rounded-lg border p-6 shadow-sm"
          variants={cardVariants}
          whileHover="hover"
        >
          <h2 className="mb-4 text-xl font-semibold">
            {t('dashboard:shopify.title')}
          </h2>
          <p className="text-base-content mb-4 text-sm">
            {t('dashboard:shopify.description')}
          </p>

          {business && (
            <>
              {isShopifyNotConnected ? (
                <>
                  <a
                    className="text-primary mb-4 block underline"
                    href="https://apps.shopify.com/t-bit-sell-more-on-whatsapp"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('dashboard:shopify.install_app')}
                  </a>

                  <div className="mb-4 flex justify-end">
                    <motion.button
                      className="hover:bg-base-200 cursor-pointer rounded p-2"
                      disabled={isLoading || isIntegrationRefreshing}
                      onClick={refreshBusinessStatus}
                      title={t('dashboard:shopify.refresh_status')}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      whileTap={{ scale: 0.9 }}
                      animate={
                        isIntegrationRefreshing
                          ? { rotate: 360 }
                          : { rotate: 0 }
                      }
                      transition={
                        isIntegrationRefreshing
                          ? { repeat: Infinity, duration: 1, ease: 'linear' }
                          : { type: 'spring', damping: 15 }
                      }
                    >
                      <ArrowPathIcon className="h-5 w-5" />
                    </motion.button>
                  </div>

                  <div className="border-base-300 bg-base-100 mb-4 flex h-20 flex-col items-center justify-center rounded-lg border px-4">
                    <AnimatePresence mode="wait">
                      {code && !isLoading && !isIntegrationRefreshing ? (
                        <motion.button
                          key="code-button"
                          className="flex items-center justify-center gap-2 p-2 text-lg font-medium tracking-widest"
                          onClick={copyCodeToClipboard}
                          title={t('dashboard:shopify.copy_code')}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          transition={{ type: 'spring', damping: 20 }}
                        >
                          <motion.span
                            initial={{ x: -10, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                          >
                            {code}
                          </motion.span>
                          <motion.div
                            whileHover={{ rotate: 15 }}
                            transition={{ type: 'spring', damping: 10 }}
                          >
                            <ClipboardIcon className="h-5 w-5" />
                          </motion.div>
                        </motion.button>
                      ) : (
                        <motion.div
                          key="loading-spinner"
                          className="flex items-center justify-center"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <motion.div
                            className="border-primary h-5 w-5 rounded-full border-b-2 border-t-2"
                            animate={{ rotate: 360 }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              ease: 'linear',
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="flex justify-between">
                    <button
                      disabled={isLoading || isIntegrationRefreshing}
                      onClick={() => {
                        getAgentDetails();
                      }}
                      className="btn btn-primary rounded-full"
                    >
                      {t('dashboard:shopify.verify_integration')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="border-base-300 bg-base-100 mb-4 flex h-20 flex-col items-center justify-center rounded-lg border px-4">
                    <p className="flex items-center gap-2 text-lg">
                      {t('dashboard:shopify.connected_to', { shopName })}
                      <CheckBadgeIcon className="h-6 w-6 text-green-500" />
                    </p>
                  </div>
                </>
              )}
            </>
          )}
        </motion.div>

        {/* Channel connections section */}
        {!integrationsLoading && enabledIntegrations.length > 0 && (
          <motion.div
            className="bg-base-100 border-base-300 flex flex-col items-center justify-center rounded-lg border p-6 shadow-sm"
            variants={cardVariants}
            whileHover="hover"
          >
            <h2 className="mb-2 text-center text-xl font-semibold">
              {isWhatsappConnected || isInstagramConnected
                ? t('dashboard:connected_channels.title')
                : t('onboarding:connect_prompt')}
            </h2>
            <motion.div
              className="mb-2 mt-2 flex flex-col justify-center gap-6 md:flex-row"
              variants={containerVariants}
            >
              {/* WhatsApp Card */}
              <motion.div
                className="border-base-300 bg-base-100 flex w-64 flex-col justify-between rounded-lg border p-4 text-left shadow-sm"
                variants={cardVariants}
                whileHover={{
                  scale: 1.02,
                  y: -4,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                }}
              >
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <WhatsAppIcon className="h-6 w-6" />
                    <h3 className="text-md text-base-content font-semibold">
                      {t('onboarding:integration_cards.whatsapp.title')}
                    </h3>
                  </div>
                  <p className="text-base-content mb-2 mt-1 flex-grow text-xs">
                    {t('onboarding:integration_cards.whatsapp.description')}
                  </p>
                  {!isWhatsappConnected && (
                    <ul className="text-base-content mb-4 list-inside list-disc pl-2 text-xs">
                      {whatsappReqList.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {isWhatsappConnected ? (
                  <div className="mt-auto">
                    {(showAllWhatsapp
                      ? whatsappIntegrations
                      : whatsappIntegrations.slice(0, 3)
                    ).map((wa: any, index: number) =>
                      (() => {
                        const channel = channelsList.find((c: any) => {
                          if (c.messaging_product !== 'whatsapp') return false;
                          return c.whatsapp_number === wa.identifier;
                        });
                        const enabled = channel?.isEnabled !== false;
                        return (
                          <div
                            key={index}
                            className={`mb-2 flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2 text-center text-sm font-black ${
                              enabled
                                ? 'border-green-200 bg-green-50 text-green-600'
                                : 'border-red-200 bg-red-50 text-red-600'
                            }`}
                          >
                            <CheckBadgeIcon
                              className={`h-5 w-5 ${
                                enabled ? 'text-green-500' : 'text-red-500'
                              }`}
                            />
                            {t('connected')}
                            {wa.identifier && (
                              <span
                                className={`${
                                  enabled ? 'text-green-700' : 'text-red-700'
                                } ml-1 text-xs`}
                              >
                                {wa.identifier}
                              </span>
                            )}
                          </div>
                        );
                      })(),
                    )}

                    {whatsappIntegrations.length > 3 && (
                      <button
                        className="mb-2 w-full text-center text-xs text-blue-600 hover:text-blue-800"
                        onClick={() => setShowAllWhatsapp(!showAllWhatsapp)}
                      >
                        {showAllWhatsapp
                          ? t('dashboard:connected_channels.show_less')
                          : t('dashboard:connected_channels.show_more', {
                              count: whatsappIntegrations.length - 3,
                            })}
                      </button>
                    )}

                    <button
                      className="btn btn-primary mt-2 w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                      onClick={() =>
                        handleConnectWhatsapp(
                          agentId,
                          registerToken,
                          navigate,
                          () => getAgentDetails(),
                        )
                      }
                    >
                      {t('dashboard:add_another_whatsapp')}
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-primary mt-auto w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                    onClick={() =>
                      handleConnectWhatsapp(
                        agentId,
                        registerToken,
                        navigate,
                        () => getAgentDetails(),
                      )
                    }
                  >
                    {t('onboarding:integration_cards.whatsapp.button')}
                  </button>
                )}
              </motion.div>

              {/* Instagram Card */}
              <motion.div
                className="border-base-300 bg-base-100 flex w-64 flex-col justify-between rounded-lg border p-4 text-left shadow-sm"
                variants={cardVariants}
                whileHover={{
                  scale: 1.02,
                  y: -4,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                }}
              >
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <InstagramIcon className="h-6 w-6" />
                    <h3 className="text-md text-base-content font-semibold">
                      {t('onboarding:integration_cards.instagram.title')}
                    </h3>
                  </div>
                  <p className="text-base-content mb-2 mt-1 flex-grow text-xs">
                    {t('onboarding:integration_cards.instagram.description')}
                  </p>
                  {!isInstagramConnected && (
                    <ul className="text-base-content mb-4 list-inside list-disc pl-2 text-xs">
                      {instagramReqList.map((item: string, idx: number) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
                {isInstagramConnected ? (
                  <div className="mt-auto">
                    {(showAllInstagram
                      ? instagramIntegrations
                      : instagramIntegrations.slice(0, 3)
                    ).map((ig: any, index: number) =>
                      (() => {
                        const channel = channelsList.find((c: any) => {
                          if (c.messaging_product !== 'instagram') return false;
                          return c?.config?.instagram_name === ig.identifier;
                        });
                        const enabled = channel?.isEnabled !== false;
                        return (
                          <div
                            key={index}
                            className={`mb-2 flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2 text-center text-sm font-black ${
                              enabled
                                ? 'border-green-200 bg-green-50 text-green-600'
                                : 'border-red-200 bg-red-50 text-red-600'
                            }`}
                          >
                            <CheckBadgeIcon
                              className={`h-5 w-5 ${
                                enabled ? 'text-green-500' : 'text-red-500'
                              }`}
                            />
                            {t('connected')}
                            {ig.identifier && (
                              <span
                                className={`${
                                  enabled ? 'text-green-700' : 'text-red-700'
                                } ml-1 text-xs`}
                              >
                                {ig.identifier}
                              </span>
                            )}
                          </div>
                        );
                      })(),
                    )}

                    {instagramIntegrations.length > 3 && (
                      <button
                        className="mb-2 w-full text-center text-xs text-blue-600 hover:text-blue-800"
                        onClick={() => setShowAllInstagram(!showAllInstagram)}
                      >
                        {showAllInstagram
                          ? t('dashboard:connected_channels.show_less')
                          : t('dashboard:connected_channels.show_more', {
                              count: instagramIntegrations.length - 3,
                            })}
                      </button>
                    )}

                    <button
                      className="btn btn-secondary mt-2 w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                      onClick={() => handleConnectInstagram(agentId)}
                    >
                      {t('dashboard:add_another_instagram')}
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn btn-secondary mt-auto w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                    onClick={() => handleConnectInstagram(agentId)}
                  >
                    {t('onboarding:integration_cards.instagram.button')}
                  </button>
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        {/* Show connection prompt only if no integrations exist */}
        {!integrationsLoading && enabledIntegrations.length === 0 && (
          <motion.div
            className="bg-base-100 border-base-300 flex flex-col items-center justify-center rounded-lg border p-6 shadow-sm"
            variants={cardVariants}
            whileHover="hover"
          >
            <h2 className="mb-2 text-center text-xl font-semibold">
              {t('onboarding:connect_prompt')}
            </h2>
            <div className="mb-2 mt-2 flex flex-col justify-center gap-6 md:flex-row">
              {/* WhatsApp Card */}
              <div className="border-base-300 bg-base-100 flex w-64 flex-col justify-between rounded-lg border p-4 text-left shadow-sm">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <WhatsAppIcon className="h-6 w-6" />
                    <h3 className="text-md text-base-content font-semibold">
                      {t('onboarding:integration_cards.whatsapp.title')}
                    </h3>
                  </div>
                  <p className="text-base-content mb-2 mt-1 flex-grow text-xs">
                    {t('onboarding:integration_cards.whatsapp.description')}
                  </p>
                  <ul className="text-base-content mb-4 list-inside list-disc pl-2 text-xs">
                    {whatsappReqList.map((item: string, idx: number) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
                <button
                  className="btn btn-primary mt-auto w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                  onClick={() =>
                    handleConnectWhatsapp(
                      agentId,
                      registerToken,
                      navigate,
                      () => getAgentDetails(),
                    )
                  }
                >
                  {t('onboarding:integration_cards.whatsapp.button')}
                </button>
              </div>

              {/* Instagram Card */}
              <div className="border-base-300 bg-base-100 flex w-64 flex-col justify-between rounded-lg border p-4 text-left shadow-sm">
                <div>
                  <div className="mb-2 flex items-center gap-3">
                    <InstagramIcon className="h-6 w-6" />
                    <h3 className="text-md text-base-content font-semibold">
                      {t('onboarding:integration_cards.instagram.title')}
                    </h3>
                  </div>
                  <p className="text-base-content mb-2 mt-1 flex-grow text-xs">
                    {t('onboarding:integration_cards.instagram.description')}
                  </p>
                  <ul className="text-base-content mb-4 list-inside list-disc pl-2 text-xs">
                    {instagramReqList.map((item: string, idx: number) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ul>
                </div>
                <button
                  className="btn btn-primary mt-auto w-full rounded-full px-3 py-2 text-sm font-black focus:outline-none focus:ring-2"
                  onClick={() => handleConnectInstagram(agentId)}
                >
                  {t('onboarding:integration_cards.instagram.button')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Quick Actions Card */}
      <motion.div
        className="bg-base-100 border-base-300 mt-6 rounded-lg border p-6 shadow-sm"
        variants={cardVariants}
        whileHover="hover"
      >
        <motion.h2
          className="mb-4 text-xl font-semibold"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, type: 'spring', damping: 20 }}
        >
          {t('dashboard:quick_actions.title')}
        </motion.h2>
        <motion.div
          className="grid grid-cols-2 gap-4"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={quickActionVariants}>
            <Link
              to={`/agent/${agentId}/${APP_DOMAINS.CONVERSATIONS}`}
              className="hover:bg-base-200 border-base-300 block flex flex-col items-center rounded-lg border p-4 transition"
            >
              <motion.span
                className="text-center"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('dashboard:quick_actions.chat')}
              </motion.span>
            </Link>
          </motion.div>
          <motion.div variants={quickActionVariants}>
            <Link
              to={`/agent/${agentId}/${APP_DOMAINS.SETTINGS}/training`}
              className="hover:bg-base-200 border-base-300 block flex flex-col items-center rounded-lg border p-4 transition"
            >
              <motion.span
                className="text-center"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('dashboard:quick_actions.settings')}
              </motion.span>
            </Link>
          </motion.div>
          <motion.div variants={quickActionVariants}>
            <Link
              to={`/agent/${agentId}/settings/faq`}
              className="hover:bg-base-200 border-base-300 block flex flex-col items-center rounded-lg border p-4 transition"
            >
              <motion.span
                className="text-center"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('dashboard:quick_actions.training')}
              </motion.span>
            </Link>
          </motion.div>
          <motion.div variants={quickActionVariants}>
            <Link
              to={`/agent/${agentId}/settings/tags`}
              className="hover:bg-base-200 border-base-300 block flex flex-col items-center rounded-lg border p-4 transition"
            >
              <motion.span
                className="text-center"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('dashboard:quick_actions.Tags')}
              </motion.span>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default AgentDashboard;
